import { z } from "zod";

import type { ProactiveRule } from "@go-novus/admin-api";

import type { ResolvedWidgetConfig } from "../config";
import type { ConversationState } from "../conversation/conversation";
import { pagePath } from "./page-context";

export interface ProactivePrompt {
  text: string;
  dismiss: () => void;
  activate: () => void;
}

/** {product_title} 换成商品名的唯一实现：替换结果是纯文本；后台预览的示例商品名走同一套。 */
export function substituteProductTitle(
  template: string,
  title: string,
): string {
  return template.replaceAll("{product_title}", () => title);
}

/**
 * 主动规则只读抑制条件、只写提示落点。入口阶段会话还没建，
 * 由挂载方给一份窄视图；聊天资源到位后同一份接口指向真实会话。
 */
export interface ProactiveSession {
  getState(): Pick<
    ConversationState,
    "open" | "busy" | "draft" | "unreadReplyId" | "unreadError" | "disabled"
  >;
  openWithPrompt(prompt: string, question: string): void;
}

const frequencySchema = z.object({
  shown: z.array(z.string()),
  nextDisplayAt: z.number(),
  hasSeenAssistant: z.boolean().default(false),
  tabKey: z.uuid().optional(),
});
type Frequency = z.infer<typeof frequencySchema>;
/** sessionStorage 不可用时的内存兜底，按店铺键分开。 */
const memoryFallback = new Map<string, Frequency>();
/** 展示一条后下一条至少间隔这么久；手动关闭的静默期更长。 */
const MIN_PROMPT_GAP_MS = 20_000;
const DISMISS_GAP_MS = 60_000;
const INTERACTIONS = [
  "scroll",
  "wheel",
  "click",
  "keydown",
  "pointerdown",
  "touchstart",
  "input",
];

/** 元素可见规则的运行时状态；since 清零即重新计时。 */
interface ElementWatch {
  invalid: boolean;
  target?: Element;
  intersecting: boolean;
  since?: number;
}

/** 不看内容，只看元素自身或链路语义上的隐藏；离屏由 IntersectionObserver 判定。 */
function concealed(element: Element): boolean {
  if (element.closest("[hidden]")) return true;
  const style = getComputedStyle(element);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.visibility === "collapse"
  )
    return true;
  // opacity 不继承：目标自身或任一祖先透明即不可见，沿 DOM 向上查。
  for (let node: Element | null = element; node; node = node.parentElement)
    if (Number.parseFloat(getComputedStyle(node).opacity) === 0) return true;
  return false;
}

/** 店面提示的计时和动作；聊天记录与发送失败仍由 Conversation 拥有。 */
export function createProactiveMessages(
  container: HTMLElement,
  config: ResolvedWidgetConfig,
  readProduct: () => ResolvedWidgetConfig["productContext"],
  conversation: ProactiveSession,
  rules: ProactiveRule[],
  onChange: () => void,
) {
  let prompt: ProactivePrompt | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let idleSince: number | undefined;
  let entrySince: number | undefined;
  let disposed = false;
  let pageSignature = "";
  const storageKey = `gonovus:proactive:${config.shopDomain}`;
  let frequency: Frequency = memoryFallback.get(storageKey) ?? {
    shown: [],
    nextDisplayAt: 0,
    hasSeenAssistant: false,
  };
  try {
    const parsed = frequencySchema.safeParse(
      JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as unknown,
    );
    if (parsed.success && !memoryFallback.has(storageKey)) {
      frequency = parsed.data;
      try {
        const opener = window.opener as Window | null;
        const source = frequencySchema.safeParse(
          JSON.parse(
            opener?.sessionStorage.getItem(storageKey) ?? "null",
          ) as unknown,
        );
        // 同源 opener 会复制 sessionStorage；新标签页只继承配置，不继承频控。
        if (
          frequency.tabKey &&
          source.success &&
          source.data.tabKey === frequency.tabKey
        )
          frequency = { shown: [], nextDisplayAt: 0, hasSeenAssistant: false };
      } catch {
        /* opener 已跨源或不允许读取时，保留当前标签页存储。 */
      }
    }
  } catch {
    frequency = memoryFallback.get(storageKey) ?? frequency;
  }

  function persist() {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(frequency));
      memoryFallback.delete(storageKey);
    } catch {
      memoryFallback.set(storageKey, frequency);
      /* 隐私模式或配额耗尽时，同一页面重新挂载仍保留内存频控。 */
    }
  }

  // 仅用于区分浏览器复制的标签页存储；不作为买家身份，也不发送到服务端。
  frequency.tabKey ??= crypto.randomUUID();
  persist();

  const watches = new Map<ProactiveRule, ElementWatch>();
  /** 共享 observer 对各目标最近一次上报的可见状态；只保留仍在观察的目标。 */
  const visibility = new Map<Element, boolean>();
  let domDirty = true;
  let intersectionObserver: IntersectionObserver | undefined;
  let domObserver: MutationObserver | undefined;

  function clearPrompt() {
    idleSince = undefined;
    entrySince = undefined;
    if (prompt) {
      prompt = undefined;
      onChange();
    }
  }

  /** 中断条件出现后，元素计时一律清零，恢复后重新积累。 */
  function resetElementTimers() {
    for (const watch of watches.values()) watch.since = undefined;
  }

  function blocked() {
    const state = conversation.getState();
    let focused = document.activeElement;
    while (focused?.shadowRoot?.activeElement)
      focused = focused.shadowRoot.activeElement;
    return (
      state.open ||
      state.busy ||
      !!state.draft ||
      // 回复提示占着入口上方的位置：不拦的话，规则会在买家看不见的时候用掉展示次数。
      !!state.unreadReplyId ||
      !!state.unreadError ||
      state.disabled ||
      document.visibilityState !== "visible" ||
      !container.isConnected ||
      container.dataset.pickerActive === "true" ||
      !!focused?.closest(
        'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
      )
    );
  }

  /**
   * 解析元素规则的当前目标：合法且唯一命中才计时，
   * 非法选择器、零命中、多命中都只是这条规则本轮不可用。
   */
  function resolveWatch(
    rule: ProactiveRule,
    now: number,
  ): ElementWatch | undefined {
    let watch = watches.get(rule);
    if (!watch) {
      watch = { invalid: false, intersecting: false };
      watches.set(rule, watch);
    }
    if (watch.invalid) return undefined;
    if (domDirty) {
      let matches: Element[];
      try {
        matches = Array.from(document.querySelectorAll(rule.selector));
      } catch {
        watch.invalid = true;
        return undefined;
      }
      const target = matches.length === 1 ? matches[0] : undefined;
      if (watch.target !== target) {
        const previous = watch.target;
        watch.target = target;
        // 目标已被其他规则观察时重复 observe 不补发回调；沿用共享可见状态，由本规则的 since 独立计时。
        watch.intersecting = target ? (visibility.get(target) ?? false) : false;
        watch.since = undefined;
        // 多条规则可以用不同选择器命中同一元素；还有其他规则在用就不能取消观察。
        if (
          previous &&
          ![...watches.values()].some((other) => other.target === previous)
        ) {
          visibility.delete(previous);
          intersectionObserver?.unobserve(previous);
        }
        if (target) intersectionObserver?.observe(target);
      }
    }
    const target = watch.target;
    if (
      !target ||
      !watch.intersecting ||
      !target.isConnected ||
      concealed(target)
    ) {
      watch.since = undefined;
      return undefined;
    }
    watch.since ??= now;
    return watch;
  }

  /** 几何只信 IntersectionObserver：有可见面积且在视口内才算看见。 */
  function onIntersect(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      const intersecting =
        entry.isIntersecting &&
        entry.intersectionRect.width > 0 &&
        entry.intersectionRect.height > 0;
      visibility.set(entry.target, intersecting);
      for (const watch of watches.values()) {
        if (watch.target !== entry.target) continue;
        watch.intersecting = intersecting;
        if (!intersecting) watch.since = undefined;
      }
    }
    sync();
  }

  function sync() {
    clearTimeout(timer);
    if (disposed) return;
    // 没有启用规则时也记录开聊，避免下一页启用进店规则后再补招呼。
    if (conversation.getState().open && !frequency.hasSeenAssistant) {
      frequency.hasSeenAssistant = true;
      persist();
    }
    const product = readProduct();
    const signature = JSON.stringify([pagePath(), product?.id]);
    if (pageSignature !== signature) {
      pageSignature = signature;
      clearPrompt();
      resetElementTimers();
      // 换页未必伴随 DOM 变更（纯 pushState）；此前被去重拦下的规则还没解析过目标。
      domDirty = true;
    }
    if (blocked()) {
      clearPrompt();
      resetElementTimers();
      return;
    }
    if (prompt) return;
    if (Date.now() < frequency.nextDisplayAt) {
      idleSince = undefined;
      entrySince = undefined;
      resetElementTimers();
      timer = setTimeout(sync, frequency.nextDisplayAt - Date.now());
      return;
    }
    const now = Date.now();
    idleSince ??= now;
    entrySince ??= now;
    const title = product?.title ?? "";
    const key = (rule: ProactiveRule) =>
      JSON.stringify([
        rule.id,
        product ? "product" : "page",
        product?.id ?? pagePath(),
      ]);
    let ready: ProactiveRule | undefined;
    let nextAt = Number.POSITIVE_INFINITY;
    for (const rule of rules) {
      if (
        !rule.enabled ||
        frequency.shown.includes(key(rule)) ||
        (!title.trim() &&
          `${rule.prompt}${rule.question}`.includes("{product_title}"))
      )
        continue;
      let at: number;
      if (rule.trigger === "product_idle") {
        if (!product) continue;
        at = idleSince + rule.waitSeconds * 1000;
      } else if (rule.trigger === "store_entry") {
        if (frequency.hasSeenAssistant || frequency.shown.length > 0) continue;
        at = entrySince + rule.waitSeconds * 1000;
      } else {
        const watch = resolveWatch(rule, now);
        if (watch?.since === undefined) continue;
        at = watch.since + rule.waitSeconds * 1000;
      }
      if (at <= now) {
        ready ??= rule;
      } else if (at < nextAt) {
        nextAt = at;
      }
    }
    domDirty = false;
    if (ready) {
      frequency = {
        ...frequency,
        shown: [...frequency.shown, key(ready)],
        nextDisplayAt: now + MIN_PROMPT_GAP_MS,
        hasSeenAssistant: true,
      };
      const text = substituteProductTitle(ready.prompt, title);
      const question = substituteProductTitle(ready.question, title);
      prompt = {
        text,
        dismiss() {
          if (!prompt) return;
          frequency.nextDisplayAt = Date.now() + DISMISS_GAP_MS;
          persist();
          clearPrompt();
          sync();
        },
        activate() {
          sync();
          if (!prompt || blocked()) return;
          prompt = undefined;
          conversation.openWithPrompt(text, question);
        },
      };
      onChange();
      persist();
    } else if (nextAt < Number.POSITIVE_INFINITY) {
      timer = setTimeout(sync, nextAt - now);
    }
  }

  const interact = () => {
    idleSince = undefined;
    sync();
  };
  const focus = () => {
    queueMicrotask(sync);
  };
  for (const event of INTERACTIONS)
    window.addEventListener(event, interact, { capture: true, passive: true });
  document.addEventListener("visibilitychange", sync);
  document.addEventListener("focusin", focus);
  document.addEventListener("focusout", focus);
  const containerObserver = new MutationObserver(sync);
  containerObserver.observe(container, {
    attributes: true,
    attributeFilter: [
      "data-picker-active",
      "data-product-id",
      "data-product-title",
      "data-variant-id",
    ],
  });
  // 动态弹窗靠整页 DOM 观察重新发现；老浏览器没有 IntersectionObserver 时元素规则静默不生效。
  if (
    rules.some((rule) => rule.enabled && rule.trigger === "element_visible") &&
    typeof IntersectionObserver === "function"
  ) {
    intersectionObserver = new IntersectionObserver(onIntersect);
    domObserver = new MutationObserver(() => {
      domDirty = true;
      sync();
    });
    // 商家手填的选择器可以挂在任意属性上，不过滤 attribute 名。
    domObserver.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  }
  const navigationEvents = [
    "popstate",
    "hashchange",
    "pageshow",
    "shopify:section:load",
  ];
  for (const event of navigationEvents) window.addEventListener(event, sync);
  // ponytail: pushState 不发浏览器事件；每 250ms 只比较 URL，主题提供导航事件后可去掉轮询。
  let href = location.href;
  const navigationTimer = setInterval(() => {
    if (location.href !== href) {
      href = location.href;
      sync();
    }
  }, 250);

  return {
    getPrompt: () => prompt,
    sync,
    dispose() {
      disposed = true;
      clearTimeout(timer);
      clearInterval(navigationTimer);
      containerObserver.disconnect();
      intersectionObserver?.disconnect();
      domObserver?.disconnect();
      for (const event of INTERACTIONS)
        window.removeEventListener(event, interact, true);
      document.removeEventListener("visibilitychange", sync);
      document.removeEventListener("focusin", focus);
      document.removeEventListener("focusout", focus);
      for (const event of navigationEvents)
        window.removeEventListener(event, sync);
    },
  };
}
